import { useState, useEffect, useCallback } from "react";
import { makeRequest } from "../assets/functions_helper";

/**
 * formatUTCDate
 *
 * Converts an ISO 8601 UTC date string (e.g. "2026-05-08T00:00:00Z")
 * to a human-readable local date string using the browser's timezone.
 *
 * @param {string|null} isoString
 * @param {string} locale — defaults to "es-CO"
 * @returns {string|null}
 */
export function formatUTCDate(isoString, locale = "es-CO") {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return null;
    return new Intl.DateTimeFormat(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  } catch {
    return null;
  }
}

/**
 * waitForActivation — Wompi async payment poller
 *
 * Polls GET /subscriptions_router/active-subscription every `intervalMs`
 * until subscription.status === 'Active', or until `maxAttempts` is reached.
 *
 * @param {object}   options
 * @param {function} options.onTick       — called on every poll with the raw response data
 * @param {number}   options.maxAttempts  — default 20 (20 × 5s = 100s max)
 * @param {number}   options.intervalMs   — default 5000 ms
 * @returns {Promise<object>} resolves with the subscription data when Active
 *                            rejects with Error("timeout") if maxAttempts exceeded
 */
export function waitForActivation({ onTick, maxAttempts = 20, intervalMs = 5000 } = {}) {
  return new Promise((resolve, reject) => {
    let attempts = 0;

    const timer = setInterval(async () => {
      attempts++;
      try {
        const data = await makeRequest(
          "GET",
          "subscriptions_router/active-subscription",
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          true // skipLoader
        );

        if (typeof onTick === "function") onTick(data);

        if (data?.status === "Active") {
          clearInterval(timer);
          resolve(data);
          return;
        }
      } catch (_) {
        // silent — keep polling on transient errors
      }

      if (attempts >= maxAttempts) {
        clearInterval(timer);
        reject(new Error("timeout"));
      }
    }, intervalMs);
  });
}

/**
 * waitForUpgradeOutcome — Upgrade charge result poller
 *
 * After POST /upgrade-subscription returns { status: "pending" }, polls
 * GET /active-subscription every `intervalMs` ms for up to `maxAttempts`
 * iterations and resolves as soon as one of three outcomes is detected:
 *
 *   - 'success':  sub.plan_tier === expectedNewTier
 *   - 'declined': sub.last_upgrade_declined === true
 *   - 'timeout':  maxAttempts reached with no conclusive outcome
 *
 * @param {object} options
 * @param {string} options.expectedNewTier  — the tier we're upgrading to (e.g. "enterprise")
 * @param {number} [options.maxAttempts=10] — default 10 (10 × 3 s = 30 s max)
 * @param {number} [options.intervalMs=3000] — polling interval in ms
 * @returns {Promise<{ outcome: 'success'|'declined'|'timeout', subscription: object|null }>}
 */
export function waitForUpgradeOutcome({ expectedNewTier, maxAttempts = 10, intervalMs = 3000 } = {}) {
  return new Promise((resolve) => {
    let attempts = 0;

    const timer = setInterval(async () => {
      attempts++;
      try {
        const data = await makeRequest(
          "GET",
          "subscriptions_router/active-subscription",
          null,
          "application/json",
          localStorage.getItem("token"),
          localStorage.getItem("tokenType"),
          null,
          true // skipLoader
        );

        // Outcome 1 — SUCCESS: plan changed to the expected tier
        if (data?.plan_tier === expectedNewTier) {
          clearInterval(timer);
          resolve({ outcome: "success", subscription: data });
          return;
        }

        // Outcome 2 — DECLINED: card was rejected, plan unchanged
        if (data?.last_upgrade_declined === true) {
          clearInterval(timer);
          resolve({ outcome: "declined", subscription: data });
          return;
        }
      } catch (_) {
        // silent — keep polling on transient network errors
      }

      // Outcome 3 — TIMEOUT: no change after maxAttempts
      if (attempts >= maxAttempts) {
        clearInterval(timer);
        resolve({ outcome: "timeout", subscription: null });
      }
    }, intervalMs);
  });
}

/**
 * useActiveSubscription
 *
 * Fetches GET /subscriptions_router/active-subscription.
 *
 * Wompi status values (use exact casing for comparisons):
 *   'Active'         — subscription is active and paid
 *   'PendingPayment' — Wompi webhook not yet confirmed
 *   'canceled'       — subscription was cancelled; access until next_billing_date
 *   'none'           — no active subscription; may include last_expired object
 *
 * Expected response shape:
 * {
 *   plan_tier:             "pro",
 *   status:                "Active" | "PendingPayment" | "canceled" | "none",
 *   amount:                49000,
 *   currency:              "COP",
 *   billing_interval:      "monthly",
 *   next_billing_date:     "2026-05-08T00:00:00Z",   ← replaces current_period_end
 *   cancellation_scheduled: false,
 *   cancellation_date:     null | "2026-05-08T00:00:00Z",
 *   scheduled_plan:        null | { plan_tier, effective_date },
 *   pending_upgrade_tier:  "enterprise" | null,        ← new: upgrade in-flight
 *   last_upgrade_declined: true | false,               ← new: last charge was declined
 *   last_expired:          null | {
 *     status:       "Suspended" | "Canceled",
 *     plan_tier:    "pro",
 *     billing_type: "monthly",
 *     amount:       99900.0,
 *     currency:     "COP",
 *     period_start: "2026-04-01T00:00:00Z",
 *     period_end:   "2026-05-01T00:00:00Z"
 *   }
 * }
 *
 * Returns:
 *   { subscription, isLoading, error, refetch, markCancelled }
 *
 *   markCancelled(accessUntilISO) — call after a successful cancel-subscription POST
 *   to update state locally without a full refetch.
 */
export function useActiveSubscription() {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading]       = useState(true);
  const [error, setError]               = useState(null);

  const fetchSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await makeRequest(
        "GET",
        "subscriptions_router/active-subscription",
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        true // skipLoader
      );
      setSubscription(data || null);
    } catch (err) {
      // 404 → no active subscription (not an error for new users)
      const msg = typeof err === "string" ? err : err?.message || "";
      if (msg.includes("404") || msg.toLowerCase().includes("no active")) {
        setSubscription(null);
      } else {
        setError("No se pudo cargar la información de tu suscripción.");
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  /**
   * Optimistically marks the subscription as cancelled locally.
   * Call this after a successful POST to cancel-subscription.
   * @param {string|null} accessUntilISO — the raw ISO UTC string from access_until
   */
  const markCancelled = useCallback((accessUntilISO) => {
    setSubscription((prev) =>
      prev
        ? { ...prev, cancellation_scheduled: true, cancellation_date: accessUntilISO }
        : prev
    );
  }, []);

  /**
   * Optimistically records a scheduled plan change locally.
   * Call this after a successful POST to downgrade-subscription.
   * @param {object} scheduledPlan — { plan_tier, effective_date } from the API response
   */
  const markDowngradeScheduled = useCallback((scheduledPlan) => {
    setSubscription((prev) =>
      prev ? { ...prev, scheduled_plan: scheduledPlan } : prev
    );
  }, []);

  return { subscription, isLoading, error, refetch: fetchSubscription, markCancelled, markDowngradeScheduled };
}
